import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { withQuietRetry } from "../utils/adminRetry";
import { PAGE_SIZE } from "../utils/adminPaging";
import AdminLoadFailed from "./AdminLoadFailed";
import { absMediaUrl } from "../utils/mediaUrl";
import { User, PayoutEvent, NewTalentResult, TalentFilter, TALENT_FILTERS, EarnStageCounts } from "../types/admin";
import {
  AdminDeleteModal,
  AdminBlockedDeleteModal,
  AdminPasswordModal,
  AdminFreezeModal,
  BlockedDelete,
  FreezeChoice,
} from "./AdminAccountModals";
import AdminPayoutsModal from "./AdminPayoutsModal";
import { AdminAddTalentModal, AdminCredentialsModal, AdminResendCredentialsModal } from "./AdminAddTalentModals";

// Talent tab — the talent table with its chip filters, and every per-talent
// account action: payout history, freeze / unfreeze, password reset, delete
// (with the can't-delete → freeze-instead hand-off), add talent and resend
// credentials. Moved out of pages/AdminDashboard.tsx in the Phase C split.

// One status per talent, the same state the talent sees in the app (server
// lib/approval.js + lib/talentMoney.js). Frozen (incl. a decline from before
// 1.5.1, when Decline was a freeze) and the admin-created "Pre-Stripe" button
// are handled in the row itself.
export function talentStatus(u: User): { label: string; cls: string } {
  if (u.declinedAt && !u.isActivated) return { label: "Declined", cls: "badge-declined" };
  if (u.earnStage ? u.earnStage === "needsBank" : !u.isStripeOnboarded) return { label: "Needs bank", cls: "badge-queued" };
  if (u.isActivated) return { label: "Earning", cls: "badge-accepted" };
  // The server's word when it sends one: a verified Instagram can be required to
  // earn (lib/approval.js), and then a typed handle is not "In review".
  const inReview = u.approvalStatus ? u.approvalStatus === "in_review" : !!(u.instagram || "").trim() || (u.links || []).some(Boolean);
  return inReview ? { label: "In review", cls: "badge-pending" } : { label: "Needs Instagram", cls: "badge-queued" };
}

const FILTER_LABEL: Record<TalentFilter, string> = {
  all: "All",
  ready: "Ready to earn",
  waitingApproval: "Waiting for approval",
  needsBank: "Needs bank",
  frozen: "Frozen",
};

// Which chip a row belongs to. Frozen is the account flag; the rest is the
// server's earnStage, or Stripe alone on a server that does not send it.
function inFilter(u: User, f: TalentFilter): boolean {
  if (f === "all") return true;
  if (f === "frozen") return !!u.isFrozen;
  if (u.earnStage) return u.earnStage === f;
  return f === "needsBank" && !u.isStripeOnboarded;
}

// The server-side talent search (server lib/admin/talents.js: q matches name,
// email, slug and Instagram; stage is the row's earnStage), or null when
// nothing narrows the list. The chip ids are the server's stage names.
export function talentSearchUrl(q: string, filter: TalentFilter): string | null {
  if (!q && filter === "all") return null;
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (q) params.set("q", q);
  if (filter !== "all") params.set("stage", filter);
  return `/users?${params.toString()}`;
}

// "18 requests · 11 fans": the talent's crowd over the last 30 days.
export function crowdLine(c: NonNullable<User["crowd30d"]>): string {
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
  return c.fans > 0 ? `${plural(c.requests, "request")} · ${plural(c.fans, "fan")}` : plural(c.requests, "request");
}

interface Props {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  usersTotal: number;
  talentStages: EarnStageCounts | null;
  loadingMoreUsers: boolean;
  loadMoreUsers: () => void;
  // The search box sits in the shell's header bar and the chip filter is
  // shell-owned too (the Growth tab's "needs bank" link sets it before
  // switching here), so both persist while the admin moves between tabs.
  userQuery: string;
  talentFilter: TalentFilter;
  setTalentFilter: (f: TalentFilter) => void;
  // "+ Add Talent" sits in the shell's header bar, so the open flag lives there.
  showAddTalent: boolean;
  setShowAddTalent: (open: boolean) => void;
  fetchAll: () => void;
  // The shell's reads (it owns the list): the first page failed, "Load more"
  // failed, the server-side search failed. Each shows "Couldn't load" +
  // Retry; rows already on screen stay.
  usersFailed: boolean;
  loadMoreFailed: boolean;
  searchFailed: boolean;
  retrySearch: () => void;
  // How many rows of the talent list itself are loaded, for "Load more (X of
  // Y)": `users` also holds a search's matches from beyond the loaded pages.
  loadedCount: number;
}

export default function AdminUsersTab({
  users,
  setUsers,
  usersTotal,
  talentStages,
  loadingMoreUsers,
  loadMoreUsers,
  userQuery,
  talentFilter,
  setTalentFilter,
  showAddTalent,
  setShowAddTalent,
  fetchAll,
  usersFailed,
  loadMoreFailed,
  searchFailed,
  retrySearch,
  loadedCount,
}: Props) {
  const navigate = useNavigate();

  // Modal state
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // Set when a talent delete is refused because they have order/payout
  // history — drives the "freeze instead" modal.
  const [blockedDelete, setBlockedDelete] = useState<
    { id: string; name: string; orderCount: number; payoutCount: number } | null
  >(null);
  const [passwordId, setPasswordId] = useState<string | null>(null);

  // Payouts panel
  const [payoutsUserId, setPayoutsUserId] = useState<string | null>(null);
  const [payoutsData, setPayoutsData] = useState<PayoutEvent[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsFailed, setPayoutsFailed] = useState(false);
  // The talent whose history the modal is showing: an answer for a talent
  // whose modal was already closed (or swapped) is dropped.
  const payoutsFor = useRef<string | null>(null);

  // Freeze modal
  const [freezeUserId, setFreezeUserId] = useState<string | null>(null);

  // Add Talent result — the temp credentials to share (also reused by the
  // resend flow). The create form itself lives in AdminAddTalentModal.
  const [newTalentResult, setNewTalentResult] = useState<NewTalentResult | null>(null);

  // Resend credentials — clicking the "Pre-Stripe" badge regenerates the temp
  // password (server can't return the original — it's bcrypt-hashed) and reuses
  // the same credentials-display modal as the create flow.
  const [resendCandidate, setResendCandidate] = useState<{ id: string; name: string } | null>(null);
  const [resendingPassword, setResendingPassword] = useState(false);

  const handlePasswordChange = async (newPassword: string) => {
    // The dialog closes only once the server took the new password; a refusal
    // (too weak, say) keeps it open with the reason.
    try {
      await adminApi.patch(`/users/${passwordId}/password`, { password: newPassword });
      setPasswordId(null);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Couldn't change the password. Try again.");
    }
  };

  const confirmDelete = async () => {
    if (!confirmId) return;
    try {
      await adminApi.delete(`/users/${confirmId}`);
      setUsers((prev) => prev.filter((x) => x._id !== confirmId));
      setConfirmId(null);
    } catch (err: any) {
      // Talent has order/payout history — the server refuses the delete.
      // Swap the confirm dialog for the "freeze instead" modal.
      if (err.response?.data?.code === "has_financial_history") {
        const u = users.find((x) => x._id === confirmId);
        setBlockedDelete({
          id: confirmId,
          name: u?.name || "This talent",
          orderCount: err.response.data.orderCount ?? 0,
          payoutCount: err.response.data.payoutCount ?? 0,
        });
        setConfirmId(null);
      } else {
        alert(err.response?.data?.msg || "Error deleting");
      }
    }
  };

  const openPayouts = async (userId: string) => {
    payoutsFor.current = userId;
    setPayoutsUserId(userId);
    setPayoutsLoading(true);
    setPayoutsFailed(false);
    try {
      const { data } = await withQuietRetry(() => adminApi.get(`/dj/${userId}/payouts`));
      if (payoutsFor.current === userId) setPayoutsData(data);
    } catch {
      if (payoutsFor.current === userId) {
        setPayoutsData([]);
        setPayoutsFailed(true);
      }
    } finally {
      if (payoutsFor.current === userId) setPayoutsLoading(false);
    }
  };

  const handleFreeze = async ({ freezeType, freezeReason, freezeUntil }: FreezeChoice) => {
    if (!freezeUserId) return;
    try {
      const { data } = await adminApi.patch(`/users/${freezeUserId}/freeze`, {
        freezeType,
        freezeReason: freezeReason || undefined,
        frozenUntil: freezeType === "temporary" && freezeUntil ? freezeUntil : undefined,
      });
      setUsers((prev) => prev.map((u) => (u._id === freezeUserId ? data.user : u)));
      setFreezeUserId(null);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error freezing account");
    }
  };

  const handleUnfreeze = async (userId: string) => {
    try {
      const { data } = await adminApi.patch(`/users/${userId}/unfreeze`);
      setUsers((prev) => prev.map((u) => (u._id === userId ? data.user : u)));
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error");
    }
  };

  const resetAddTalent = () => {
    setShowAddTalent(false);
    setNewTalentResult(null);
  };

  const confirmRegenerateTempPassword = async () => {
    if (!resendCandidate) return;
    setResendingPassword(true);
    try {
      const { data } = await adminApi.post<NewTalentResult>(
        `/users/${resendCandidate.id}/regenerate-temp-password`
      );
      setResendCandidate(null);
      setNewTalentResult(data); // reuses the existing credentials-display modal
    } catch (err: any) {
      alert(err.response?.data?.msg || "Could not generate new password");
    } finally {
      setResendingPassword(false);
    }
  };

  // Users search
  const q = userQuery.trim().toLowerCase();
  const filteredUsers = useMemo(() => {
    let scoped = talentFilter === "all" ? users : users.filter((u) => inFilter(u, talentFilter));
    // No bank: the talent whose crowd is already requesting come first.
    if (talentFilter === "needsBank") scoped = [...scoped].sort((a, b) => (b.crowd30d?.requests || 0) - (a.crowd30d?.requests || 0));
    if (!q) return scoped;
    // Instagram too: the server's search matches it (a handle typed with or
    // without the @).
    const handle = q.replace(/^@/, "");
    return scoped.filter(
      (u) => [u.name, u.email, u.slug].some((v) => (v || "").toLowerCase().includes(q)) || (!!handle && (u.instagram || "").toLowerCase().includes(handle))
    );
  }, [users, q, talentFilter]);
  // Server counts cover every talent; the loaded rows are the fallback for a
  // server that does not send them yet.
  const talentCounts = useMemo(() => {
    const counts = {} as Record<TalentFilter, number>;
    for (const f of TALENT_FILTERS) {
      counts[f] = f === "all" ? usersTotal : talentStages ? talentStages[f] : users.filter((u) => inFilter(u, f)).length;
    }
    return counts;
  }, [users, usersTotal, talentStages]);

  const payoutsUser = users.find((u) => u._id === payoutsUserId);

  return (
    <>
      {/* Never "No Talent found" (or chips of zeros) for a list that didn't
          load. + Add Talent still works: its modals render below. */}
      {usersFailed && users.length === 0 ? (
        <section className="fade-in">
          <AdminLoadFailed what="talent" onRetry={fetchAll} />
        </section>
      ) : (
      <section className="fade-in">
        {usersFailed && <AdminLoadFailed what="the latest talent list" onRetry={fetchAll} />}
        {searchFailed && <AdminLoadFailed what="all matching talent" onRetry={retrySearch} />}
        {talentStages && usersTotal > 0 && (
          <p className="adm-fs-88" style={{ margin: "0 0 10px" }}>
            <b>
              {talentStages.ready} of {usersTotal}
            </b>{" "}
            talent can take paid requests · {Math.round((talentStages.ready / usersTotal) * 100)}%
            {!!talentStages.needsBankWithRequests && (
              <>
                {" · "}
                <button
                  type="button"
                  onClick={() => setTalentFilter("needsBank")}
                  style={{ background: "none", border: 0, padding: 0, font: "inherit", color: "var(--adm-blue)", cursor: "pointer" }}
                >
                  {talentStages.needsBankWithRequests} without a bank got requests in the last 30 days
                </button>
              </>
            )}
          </p>
        )}
        <div className="adm-chips">
          {TALENT_FILTERS.map((f) => (
            <button key={f} type="button" className={talentFilter === f ? "on" : ""} onClick={() => setTalentFilter(f)}>
              {FILTER_LABEL[f]} {talentCounts[f]}
            </button>
          ))}
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table-light">
            <thead>
              <tr>
                <th>Talent</th>
                <th>Email</th>
                <th>Status</th>
                <th style={{ width: 380 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const avatarSrc = absMediaUrl(u.avatarUrl);
                return (
                  <tr key={u._id} className={u.isFrozen ? "row-frozen" : ""}>
                    <td>
                      <div
                        className="dj-cell clickable"
                        onClick={() => navigate(`/admin/dj/${u._id}`)}
                      >
                        {avatarSrc ? (
                          <img src={avatarSrc} className="dj-cell-avatar" alt="" />
                        ) : (
                          <div className="dj-cell-placeholder">{u.name[0].toUpperCase()}</div>
                        )}
                        <div>
                          <span className="dj-link-text adm-fs-88 adm-semibold">
                            {u.name}
                          </span>
                          {u.slug && (
                            <div className="muted-text adm-fs-72">
                              @{u.slug}
                            </div>
                          )}
                          {u.bankLast4 && (
                            <div
                              className="muted-text adm-fs-72"
                              style={{ marginTop: 2 }}
                              title={u.bankBrand ? `${u.bankBrand} ••${u.bankLast4}` : undefined}
                            >
                              🏦 ••{u.bankLast4}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="muted-text adm-fs-85">
                      {u.email}
                    </td>
                    <td>
                      {u.isFrozen && u.freezeReason === "application_declined" ? (
                        <span className="badge badge-sm badge-declined">Declined</span>
                      ) : u.isFrozen ? (
                        <div>
                          <span className="badge badge-sm badge-frozen">
                            {u.freezeType === "permanent" ? "Frozen" : "Temp. Frozen"}
                          </span>
                          {u.frozenUntil && (
                            <div className="muted-text adm-fs-70" style={{ marginTop: 2 }}>
                              Until {new Date(u.frozenUntil).toLocaleDateString()}
                            </div>
                          )}
                          {u.freezeReason && (
                            <div className="muted-text adm-fs-70">
                              {u.freezeReason}
                            </div>
                          )}
                        </div>
                      ) : !u.isStripeOnboarded && u.createdByAdminId ? (
                        // Admin-created + not yet Stripe-onboarded: intentional state.
                        // Clickable — opens a confirm modal to regenerate the temp
                        // password (the original is bcrypt-only, can't be recovered).
                        <button
                          type="button"
                          className="badge badge-sm adm-pointer"
                          onClick={() => setResendCandidate({ id: u._id, name: u.name })}
                          style={{
                            background: "#fef3c7",
                            color: "#92400e",
                            border: "1px solid #fcd34d",
                            font: "inherit",
                            padding: "2px 8px",
                          }}
                          title="Click to resend credentials (generates a new temp password)"
                        >
                          Pre-Stripe
                        </button>
                      ) : (
                        <span className={`badge badge-sm ${talentStatus(u).cls}`}>{talentStatus(u).label}</span>
                      )}
                      {u.crowd30d && (
                        <div className="muted-text adm-fs-72" style={{ marginTop: 4 }} title="Last 30 days, not counting the talent's own tests">
                          {crowdLine(u.crowd30d)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-btn-group">
                        <button
                          className="adm-btn-sm adm-btn-outline"
                          onClick={() => openPayouts(u._id)}
                        >
                          Payouts
                        </button>
                        {u.isFrozen ? (
                          <button
                            className="adm-btn-sm adm-btn-green-sm"
                            onClick={() => handleUnfreeze(u._id)}
                          >
                            Unfreeze
                          </button>
                        ) : (
                          <button
                            className="adm-btn-sm adm-btn-orange-sm"
                            onClick={() => setFreezeUserId(u._id)}
                          >
                            Freeze
                          </button>
                        )}
                        <button
                          className="adm-btn-sm adm-btn-blue-sm"
                          onClick={() => setPasswordId(u._id)}
                        >
                          Password
                        </button>
                        <button
                          className="adm-btn-sm adm-btn-red-sm"
                          title="Delete this DJ's entire account — both their Stripe Connect account and their User record. They'd need to sign up fresh."
                          onClick={() => setConfirmId(u._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="adm-center adm-p-30 adm-grey">
                    No Talent found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loadedCount < usersTotal && loadMoreFailed && (
            <AdminLoadFailed what="more talent" onRetry={loadMoreUsers} />
          )}
          {loadedCount < usersTotal && !loadMoreFailed && (
            <button
              className="adm-btn-light adm-center-block"
              disabled={loadingMoreUsers}
              onClick={loadMoreUsers}
            >
              {loadingMoreUsers ? "Loading…" : `Load more (${loadedCount} of ${usersTotal})`}
            </button>
          )}
        </div>
      </section>
      )}
      {confirmId && <AdminDeleteModal onCancel={() => setConfirmId(null)} onConfirm={confirmDelete} />}
      {blockedDelete && (
        <AdminBlockedDeleteModal
          blockedDelete={blockedDelete as BlockedDelete}
          onCancel={() => setBlockedDelete(null)}
          onFreezeInstead={() => {
            setFreezeUserId(blockedDelete.id);
            setBlockedDelete(null);
          }}
        />
      )}
      {passwordId && <AdminPasswordModal onCancel={() => setPasswordId(null)} onSave={handlePasswordChange} />}
      {freezeUserId && (
        <AdminFreezeModal
          userName={users.find((u) => u._id === freezeUserId)?.name}
          onCancel={() => setFreezeUserId(null)}
          onConfirm={handleFreeze}
        />
      )}
      {payoutsUserId && (
        <AdminPayoutsModal
          payoutsUser={payoutsUser}
          payoutsLoading={payoutsLoading}
          payoutsData={payoutsData}
          payoutsFailed={payoutsFailed}
          onRetry={() => openPayouts(payoutsUserId)}
          onClose={() => {
            payoutsFor.current = null;
            setPayoutsUserId(null);
          }}
        />
      )}
      {resendCandidate && (
        <AdminResendCredentialsModal
          resendCandidate={resendCandidate}
          resendingPassword={resendingPassword}
          onCancel={() => setResendCandidate(null)}
          onConfirm={confirmRegenerateTempPassword}
        />
      )}
      {showAddTalent && !newTalentResult && (
        <AdminAddTalentModal
          onClose={resetAddTalent}
          onCreated={(data) => {
            setNewTalentResult(data);
            fetchAll();
          }}
        />
      )}
      {newTalentResult && <AdminCredentialsModal newTalentResult={newTalentResult} onDone={resetAddTalent} />}
    </>
  );
}
